// client/src/pages/IssueDetailPage.jsx
import { useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Camera, Film, X, Trash2, Briefcase, Loader2, Wrench, Building2, CalendarClock } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import {
  useIssue, useUpdateIssue, useAddIssuePhoto, useDeleteIssuePhoto,
  useCreateJobFromIssue, useIssues, useRaiseJobRequest, useIssueUpdates,
} from '../api/hooks'
import CalendarPicker from '../components/ui/CalendarPicker'

// ─── Constants ────────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  low:      { badge: 'bg-blue-500/15 text-blue-400 border-blue-500/20',     dot: 'bg-blue-400'    },
  medium:   { badge: 'bg-amber-500/15 text-amber-400 border-amber-500/20',  dot: 'bg-amber-400'   },
  high:     { badge: 'bg-orange-500/15 text-orange-400 border-orange-500/20', dot: 'bg-orange-400' },
  critical: { badge: 'bg-red-500/15 text-red-400 border-red-500/20',        dot: 'bg-red-400'     },
}

const STATUS_STYLES = {
  open:        'bg-blue-500/15 text-blue-400',
  quoted:      'bg-purple-500/15 text-purple-400',
  approved:    'bg-indigo-500/15 text-indigo-400',
  in_progress: 'bg-amber-500/15 text-amber-400',
  resolved:    'bg-green-500/15 text-green-400',
  deferred:    'bg-gray-500/15 text-gray-400',
}

const STATUS_LABELS = {
  open: 'Open', quoted: 'Quoted', approved: 'Approved',
  in_progress: 'In Progress', resolved: 'Resolved', deferred: 'Deferred',
}

const SEVERITY_STYLES_BADGE = {
  low: 'bg-blue-500/15 text-blue-400', medium: 'bg-amber-500/15 text-amber-400',
  high: 'bg-orange-500/15 text-orange-400', critical: 'bg-red-500/15 text-red-400',
}

const STATUS_OPTIONS = ['open', 'in_progress', 'resolved', 'deferred', 'quoted']

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

// ─── Plate ────────────────────────────────────────────────────────────────────

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-sm
                     bg-yellow-400 text-black px-3 py-1 rounded">
      {registration}
    </span>
  )
}

// ─── Repair Updates constants ─────────────────────────────────────────────────

const JOB_UPDATE_STYLES = {
  requested:         'bg-blue-500/15 text-blue-400',
  quoted:            'bg-purple-500/15 text-purple-400',
  awaiting_approval: 'bg-yellow-500/15 text-yellow-400',
  approved:          'bg-indigo-500/15 text-indigo-400',
  in_progress:       'bg-amber-500/15 text-amber-400',
  waiting_on_parts:  'bg-orange-500/15 text-orange-400',
  completed:         'bg-green-500/15 text-green-400',
}

const JOB_UPDATE_LABELS = {
  requested:         'Requested',
  quoted:            'Quote Ready',
  awaiting_approval: 'Awaiting Approval',
  approved:          'Approved',
  in_progress:       'In Progress',
  waiting_on_parts:  'Waiting on Parts',
  completed:         'Complete',
}

const JOB_UPDATE_MESSAGES = {
  requested:         'Your request is being reviewed by the garage.',
  quoted:            'A quote has been prepared — visit Repairs to review it.',
  awaiting_approval: 'Work is awaiting your approval.',
  approved:          'Approved — waiting to be scheduled.',
  in_progress:       'Work is currently in progress.',
  waiting_on_parts:  'Waiting for parts to arrive before work can continue.',
  completed:         'Repair complete.',
}

const PART_STATUS_STYLES = {
  required: 'bg-gray-500/15 text-gray-400',
  ordered:  'bg-blue-500/15 text-blue-400',
  received: 'bg-indigo-500/15 text-indigo-400',
  fitted:   'bg-green-500/15 text-green-400',
  returned: 'bg-gray-500/15 text-gray-400',
}

const PART_STATUS_LABELS = {
  required: 'Needed',
  ordered:  'Ordered',
  received: 'In Stock',
  fitted:   'Fitted',
  returned: 'Returned',
}

// ─── Repair Updates component (customer only) ─────────────────────────────────

function RepairUpdates({ issueId }) {
  const { data: jobs = [], isLoading } = useIssueUpdates(issueId)

  if (isLoading) {
    return (
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5 space-y-3">
        <div className="h-4 w-32 bg-[var(--bg-overlay)] rounded animate-pulse" />
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-10 bg-[var(--bg-overlay)] rounded-lg animate-pulse" />
        ))}
      </div>
    )
  }

  if (jobs.length === 0) return null

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
      <h3 className="font-medium text-[var(--text-primary)] mb-4">Repair Updates</h3>
      <div className="space-y-5">
        {jobs.map((job, idx) => (
          <div key={job.id}>
            {idx > 0 && <div className="border-t border-[var(--border)] mb-5" />}

            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)] truncate">{job.title}</p>
                {job.garage_name && (
                  <p className="text-xs text-[var(--text-muted)]">{job.garage_name}</p>
                )}
              </div>
              <span className={clsx('shrink-0 px-2 py-0.5 rounded text-xs font-medium',
                JOB_UPDATE_STYLES[job.status] || 'bg-gray-500/15 text-gray-400')}>
                {JOB_UPDATE_LABELS[job.status] || job.status}
              </span>
            </div>

            {JOB_UPDATE_MESSAGES[job.status] && (
              <p className="text-sm text-[var(--text-secondary)] mb-2 leading-relaxed">
                {JOB_UPDATE_MESSAGES[job.status]}
              </p>
            )}

            {job.scheduled_date && (
              <div className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] mb-2">
                <CalendarClock size={12} />
                Scheduled:{' '}
                {new Date(job.scheduled_date).toLocaleDateString('en-GB', {
                  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                })}
                {job.scheduled_time && ` at ${job.scheduled_time.slice(0, 5)}`}
              </div>
            )}

            {job.parts.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[var(--border)]">
                <p className="text-xs font-medium text-[var(--text-muted)] mb-2">Parts</p>
                <div className="space-y-1.5">
                  {job.parts.map(part => (
                    <div key={part.id} className="flex items-center justify-between gap-2">
                      <span className="text-sm text-[var(--text-secondary)] truncate">
                        {part.name}
                        {part.quantity > 1 && (
                          <span className="text-[var(--text-muted)]"> ×{part.quantity}</span>
                        )}
                      </span>
                      <span className={clsx('shrink-0 px-1.5 py-0.5 rounded text-xs font-medium',
                        PART_STATUS_STYLES[part.status] || 'bg-gray-500/15 text-gray-400')}>
                        {PART_STATUS_LABELS[part.status] || part.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Raise Job modal (customer) ───────────────────────────────────────────────

function RaiseJobModal({ issue, onClose }) {
  const raiseJob = useRaiseJobRequest()
  const { data: allIssues = [] } = useIssues({ vehicleId: issue.vehicle_id })

  const related = allIssues.filter(i =>
    i.id !== issue.id &&
    i.vehicle_id === issue.vehicle_id &&
    ['open', 'deferred'].includes(i.status)
  )

  const [extraIds,      setExtraIds]      = useState([])
  const [message,       setMessage]       = useState('')
  const [preferredDate, setPreferredDate] = useState('')

  const toggle = (id) =>
    setExtraIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const allSelected = [issue.id, ...extraIds]

  const derivedTitle = allSelected.length === 1
    ? `Fix: ${issue.title}`
    : `Fix: ${allSelected.length} issues — ${issue.title}`

  const hasGarage = !!issue.primary_garage_id

  const handleSubmit = async () => {
    try {
      await raiseJob.mutateAsync({
        vehicleId:    issue.vehicle_id,
        title:        derivedTitle,
        description:  message.trim() || null,
        issueIds:     allSelected,
        preferredDate: preferredDate || null,
      })
      toast.success('Job request sent to your garage')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit request')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Request Repair</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Primary issue */}
          <div>
            <p className="text-xs font-medium text-[var(--text-muted)] mb-2">Reporting issue</p>
            <div className="flex items-center gap-2 px-4 py-3 bg-[var(--bg-base)] border border-indigo-500/30 rounded-xl">
              {issue.registration && <Plate registration={issue.registration} />}
              <span className="text-sm font-medium text-[var(--text-primary)]">{issue.title}</span>
            </div>
          </div>

          {/* Additional issues */}
          {related.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--text-muted)] mb-2">
                Also include from same vehicle
              </p>
              <div className="space-y-1.5">
                {related.map(i => (
                  <label key={i.id}
                    className="flex items-center gap-3 px-4 py-2.5 bg-[var(--bg-base)]
                               border border-[var(--border)] rounded-xl cursor-pointer
                               hover:bg-[var(--bg-overlay)] transition-colors">
                    <input
                      type="checkbox"
                      checked={extraIds.includes(i.id)}
                      onChange={() => toggle(i.id)}
                      className="accent-indigo-500 shrink-0"
                    />
                    <span className="text-sm text-[var(--text-primary)] flex-1">{i.title}</span>
                    <span className={clsx('shrink-0 px-1.5 py-0.5 rounded text-xs font-medium',
                      SEVERITY_STYLES_BADGE[i.severity] || 'bg-gray-500/15 text-gray-400')}>
                      {i.severity || 'low'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Garage */}
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-base)]">
            <Building2 size={14} className={hasGarage ? 'text-indigo-400' : 'text-[var(--text-muted)]'} />
            {hasGarage ? (
              <div>
                <p className="text-xs text-[var(--text-muted)]">Sending to</p>
                <p className="text-sm font-medium text-[var(--text-primary)]">{issue.primary_garage_name}</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-amber-400">No garage registered</p>
                <p className="text-xs text-[var(--text-muted)]">
                  Go to Settings → My Garage to register your vehicle first.
                </p>
              </div>
            )}
          </div>

          {/* Preferred date */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5">
              Preferred repair date (optional)
            </label>
            <CalendarPicker
              value={preferredDate}
              onChange={setPreferredDate}
              minDate={new Date()}
              placeholder="Pick a preferred date…"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5">
              Message to garage (optional)
            </label>
            <textarea
              rows={2}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Any additional details for the garage…"
              className={clsx(inputCls, 'resize-none')}
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!hasGarage || raiseJob.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40
                         text-white text-sm font-medium rounded-lg transition-colors
                         flex items-center justify-center gap-2">
              {raiseJob.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Sending…</>
                : <><Wrench size={14} /> Submit Request</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function IssueDetailPage() {
  const { id }         = useParams()
  const navigate       = useNavigate()
  const { user }       = useAuth()
  const { data: issue, isLoading, error } = useIssue(id)
  const updateIssue        = useUpdateIssue()
  const addPhoto           = useAddIssuePhoto()
  const deletePhoto        = useDeleteIssuePhoto()
  const createJobFromIssue = useCreateJobFromIssue()
  const fileInputRef       = useRef(null)

  const [editingNotes, setEditingNotes] = useState(false)
  const [notesValue,   setNotesValue]   = useState('')
  const [showRaiseJob, setShowRaiseJob] = useState(false)

  const isGarage   = ['garage_user', 'garage_admin', 'platform_admin'].includes(user?.role)
  const isCustomer = user?.role === 'customer'

  // ── Status update ──────────────────────────────────────────────────────────

  const handleStatusChange = async (status) => {
    try {
      await updateIssue.mutateAsync({ id, status })
      toast.success('Status updated')
    } catch {
      toast.error('Update failed')
    }
  }

  // ── Photo upload ───────────────────────────────────────────────────────────

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    let ok = 0
    for (const file of files) {
      try {
        await addPhoto.mutateAsync({ issueId: id, file })
        ok++
      } catch (err) {
        toast.error(err.response?.data?.error || `Failed to upload ${file.name}`)
      }
    }
    if (ok > 0) toast.success(ok === 1 ? 'Media added' : `${ok} files added`)
    e.target.value = ''
  }

  const handleDeletePhoto = async (photo) => {
    const label = photo.mime_type?.startsWith('video/') ? 'video' : 'photo'
    if (!window.confirm(`Remove this ${label}?`)) return
    try {
      await deletePhoto.mutateAsync({ issueId: id, photoId: photo.id })
      toast.success('Media removed')
    } catch {
      toast.error('Remove failed')
    }
  }

  // ── Internal notes save ────────────────────────────────────────────────────

  const handleSaveNotes = async () => {
    try {
      await updateIssue.mutateAsync({ id, internalNotes: notesValue })
      toast.success('Notes saved')
      setEditingNotes(false)
    } catch {
      toast.error('Save failed')
    }
  }

  // ── Create job (garage) ────────────────────────────────────────────────────

  const handleCreateJob = async () => {
    try {
      const job = await createJobFromIssue.mutateAsync(id)
      toast.success('Job created')
      navigate(`/garage/jobs/${job.id}`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create job')
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse max-w-2xl">
        <div className="h-8 w-32 bg-[var(--bg-surface)] rounded-lg" />
        <div className="h-40 bg-[var(--bg-surface)] rounded-xl" />
        <div className="h-48 bg-[var(--bg-surface)] rounded-xl" />
      </div>
    )
  }

  if (error || !issue) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/issues')}
          className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          <ArrowLeft size={16} /> Issues
        </button>
        <p className="text-[var(--text-secondary)]">Issue not found.</p>
      </div>
    )
  }

  const severity    = issue.severity || 'low'
  const sevStyles   = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.low
  const statusStyle = STATUS_STYLES[issue.status] ?? 'bg-gray-500/15 text-gray-400'

  const canRaiseJob = isCustomer &&
    ['open', 'deferred'].includes(issue.status) &&
    issue.primary_garage_id

  return (
    <div className="space-y-6 pb-12 max-w-2xl">
      {/* Back */}
      <button
        onClick={() => navigate('/issues')}
        className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ArrowLeft size={16} />
        Issues
      </button>

      {/* Header */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">
            {issue.title}
          </h1>
          <span className={clsx('px-2.5 py-1 rounded text-xs font-medium border shrink-0 mt-1', sevStyles.badge)}>
            {severity}
          </span>
        </div>

        {/* Vehicle + status */}
        <div className="flex items-center gap-3 flex-wrap mb-4">
          {issue.registration && <Plate registration={issue.registration} />}
          {issue.make && (
            <span className="text-sm text-[var(--text-secondary)]">
              {issue.make} {issue.model}
            </span>
          )}
          <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', statusStyle)}>
            {STATUS_LABELS[issue.status] || issue.status}
          </span>
        </div>

        <p className="text-xs text-[var(--text-muted)]">
          Identified:{' '}
          {new Date(issue.date_identified).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'long', year: 'numeric',
          })}
          {issue.date_resolved && (
            <> · Resolved:{' '}
              {new Date(issue.date_resolved).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}
            </>
          )}
        </p>

        {issue.description && (
          <p className="text-sm text-[var(--text-secondary)] mt-4 leading-relaxed">
            {issue.description}
          </p>
        )}

        {/* Garage — create job (garage staff) */}
        {isGarage && !['in_progress', 'resolved'].includes(issue.status) && (
          <div className="mt-4 pt-4 border-t border-[var(--border)]">
            <button
              onClick={handleCreateJob}
              disabled={createJobFromIssue.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
                         disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {createJobFromIssue.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Creating…</>
                : <><Briefcase size={14} /> Create Job</>}
            </button>
          </div>
        )}

        {/* Customer — request repair */}
        {isCustomer && ['open', 'deferred'].includes(issue.status) && (
          <div className="mt-4 pt-4 border-t border-[var(--border)]">
            {canRaiseJob ? (
              <button
                onClick={() => setShowRaiseJob(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Wrench size={14} />
                Request Repair
              </button>
            ) : (
              <div className="flex items-center gap-2 text-amber-400">
                <Building2 size={14} />
                <p className="text-xs">
                  Register your vehicle with a garage (Settings → My Garage) to request a repair.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Repair Updates — customer only */}
      {isCustomer && <RepairUpdates issueId={id} />}

      {/* Status management — garage only */}
      {isGarage && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
          <h3 className="font-medium text-[var(--text-primary)] mb-3">Update Status</h3>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => handleStatusChange(s)}
                disabled={issue.status === s || updateIssue.isPending}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50',
                  issue.status === s
                    ? 'bg-indigo-600 text-white cursor-default'
                    : 'bg-[var(--bg-overlay)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]'
                )}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Media */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[var(--text-primary)]">
            Media {issue.photos?.length > 0 && <span className="text-[var(--text-muted)] font-normal">({issue.photos.length})</span>}
          </h3>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={addPhoto.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                       bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                       text-[var(--text-secondary)] hover:text-[var(--text-primary)]
                       rounded-lg transition-colors disabled:opacity-50"
          >
            <Camera size={13} />
            {addPhoto.isPending ? 'Uploading…' : 'Add Media'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={handlePhotoSelect}
          />
        </div>

        {!issue.photos?.length ? (
          <p className="text-sm text-[var(--text-muted)]">No media attached.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {issue.photos.map(photo => {
              const isVideo = photo.mime_type?.startsWith('video/')
              return (
                <div key={photo.id} className="relative group rounded-lg overflow-hidden bg-[var(--bg-overlay)] aspect-square">
                  {isVideo ? (
                    <>
                      <video
                        src={photo.url}
                        className="w-full h-full object-cover"
                        preload="metadata"
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="p-2 bg-black/50 rounded-full">
                          <Film size={18} className="text-white" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img
                      src={photo.url}
                      alt={photo.filename || 'Issue photo'}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <button
                      onClick={() => handleDeletePhoto(photo)}
                      className="opacity-0 group-hover:opacity-100 p-2 bg-red-600 rounded-full text-white transition-opacity"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Internal notes — garage only */}
      {isGarage && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-[var(--text-primary)]">Internal Notes</h3>
            {!editingNotes ? (
              <button
                onClick={() => { setNotesValue(issue.internal_notes || ''); setEditingNotes(true) }}
                className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={handleSaveNotes} disabled={updateIssue.isPending}
                  className="text-xs text-indigo-400 hover:text-indigo-300 disabled:opacity-50">
                  Save
                </button>
                <button onClick={() => setEditingNotes(false)} className="text-xs text-[var(--text-muted)]">
                  Cancel
                </button>
              </div>
            )}
          </div>

          {editingNotes ? (
            <textarea
              rows={4}
              value={notesValue}
              onChange={e => setNotesValue(e.target.value)}
              placeholder="Notes visible to garage staff only…"
              className={clsx(inputCls, 'resize-none')}
            />
          ) : issue.internal_notes ? (
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">
              {issue.internal_notes}
            </p>
          ) : (
            <p className="text-sm text-[var(--text-muted)]">No internal notes.</p>
          )}
        </div>
      )}

      {showRaiseJob && (
        <RaiseJobModal issue={issue} onClose={() => setShowRaiseJob(false)} />
      )}
    </div>
  )
}
